/**
 * The discovery book (docs/spells-depth.md 5): which woven pieces and Grand
 * Magicks this wizard has made, kept in localStorage like the keymap.
 * Only your own finds count, even in a shared room (Bennett, 2026-10-28:
 * "in a shared room no it shouldnt show for other people"); `ownFind`
 * decides. A book filled before that change keeps everything in it.
 *
 * Every storage access is guarded: private windows and blocked storage
 * still play, they just forget.
 *
 * The cheat code (Bennett, 2026-11-02: "reveal them all for 2 minutes", to test
 * the spells): typed in the chat box, it opens the whole book for REVEAL_MS.
 * Nothing about it is saved and nobody else sees it: when it runs out the book
 * is back to what you have really found, plus anything you made meanwhile.
 */

import type { Element, GrandId } from '@shared/index.js';
import { GRAND_IDS, WOVEN_LIST } from '@shared/index.js';

const STORAGE_KEY = 'spellweave.book';

export const REVEAL_MS = 2 * 60 * 1000;
const REVEAL_CODE = 'iknowkungfu';

/** Is this chat line the cheat code? Spaces and capitals don't matter. */
export function isRevealCode(text: string): boolean {
  return text.toLowerCase().replace(/\s+/g, '') === REVEAL_CODE;
}

export interface BookStorage { getItem(k: string): string | null; setItem(k: string, v: string): void }

export class Discoveries {
  private readonly pieces = new Set<Element>();
  private readonly grands = new Set<GrandId>();
  private revealUntil = 0;

  constructor(private readonly storage: BookStorage | null) {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      const { p, g } = parsed as { p?: unknown; g?: unknown };
      if (Array.isArray(p)) for (const el of p) if ((WOVEN_LIST as readonly unknown[]).includes(el)) this.pieces.add(el as Element);
      if (Array.isArray(g)) for (const id of g) if ((GRAND_IDS as readonly unknown[]).includes(id)) this.grands.add(id as GrandId);
    } catch {
      /* unreadable: start a fresh book */
    }
  }

  knows(el: Element, now = Date.now()): boolean {
    return this.pieces.has(el) || this.revealedFor(now) > 0;
  }

  /** Open the whole book for a while. */
  reveal(now = Date.now()): void {
    this.revealUntil = now + REVEAL_MS;
  }

  /** How many ms the whole book stays open, 0 when it isn't. */
  revealedFor(now = Date.now()): number {
    return Math.max(0, this.revealUntil - now);
  }

  hasCast(id: GrandId): boolean {
    return this.grands.has(id);
  }

  /** Record a piece; true the first time. */
  addPiece(el: Element): boolean {
    if (this.pieces.has(el) || !(WOVEN_LIST as readonly Element[]).includes(el)) return false;
    this.pieces.add(el);
    this.save();
    return true;
  }

  /** Record a cast Grand Magick; true the first time. */
  addGrand(id: GrandId): boolean {
    if (this.grands.has(id)) return false;
    this.grands.add(id);
    this.save();
    return true;
  }

  get pieceCount(): number { return this.pieces.size; }
  get grandCount(): number { return this.grands.size; }

  private save(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify({ p: [...this.pieces], g: [...this.grands] }));
    } catch {
      /* full or blocked storage: the book still fills for this session */
    }
  }
}

/** Does a `woven` or `grand` event go in this wizard's book? Only when they made it themselves. */
export function ownFind(ev: { p: number }, localPid: number): boolean {
  return localPid >= 0 && ev.p === localPid;
}

/** The page's book, or one that forgets (node tests, blocked storage). */
export const discoveries = new Discoveries(((): BookStorage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
})());
